// ABOUTME: Dashboard-mode FULL pivot cell — name + slim header (filter, mode toggle, settings gear holding
// ABOUTME: heatmap/trends) + the real pivot grid with row drill-through. Its data is computed live via /details.
import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "../../../../theme";
import Subheading from "../../../theme/text/Subheading";
import { ReportHubFilterProvider, useReportHubFilters } from "../ReportHubFilterContext";
import { RhFilter } from "./RhFilter";
import { RhPivotTable } from "./RhPivotTable";
import { RhPivotSettings } from "./RhPivotSettings";
import { RhPivotModeToggle } from "./pivottable/RhPivotModeToggle";
import { getReportForEnvironment, getReportKey } from "../reports";
import { getPivotView, purgeNonSerializable } from "../reportUtils";
import { useGetBiDetailsByIdQuery, useGetBiDetails2Query } from "../../../../newapi/global/businessIntelligence";
import { RhDateSelector } from "./RhDateSelector";

const DEFAULT_DATE = { type: "relative", days: 30, frame: "1D" };

const RhDashboardPivotFullInner = ({ chart }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const filtersRegistry = useReportHubFilters();

    const reportId = chart?.reportId;
    const [currentReport, setCurrentReport] = useState(null);
    // The full pivot is live and owns its own date (the dashboard-wide date control was removed).
    const [dateReport, setDateReport] = useState({ date: DEFAULT_DATE });
    const { data: saved } = useGetBiDetailsByIdQuery(reportId, { skip: !reportId });

    useEffect(() => {
        const cfg = saved?.savedReport?.config;
        if (!cfg) return;
        setCurrentReport(cfg);
        filtersRegistry.setCurrentReportKey?.(cfg.type);
        filtersRegistry.setChartSettings?.(getReportForEnvironment(getReportKey(cfg), filtersRegistry.environment));
    }, [saved]);

    // FULL pivots are computed live: the report's saved config + the dashboard-wide date -> /details.
    const queryBody = useMemo(() => {
        if (!currentReport) return null;
        const { displayType, name, ...rest } = currentReport;
        // The cell's own date control drives the live query.
        return purgeNonSerializable({ ...rest, date: dateReport.date });
    }, [currentReport, dateReport]);

    const { data: biData, isLoading, isError } = useGetBiDetails2Query(
        { args: { page: 1, size: 100, body: queryBody } },
        { skip: !queryBody }
    );

    const chartOption = getReportForEnvironment(getReportKey(currentReport), filtersRegistry.environment);
    const name = currentReport?.name || chart?.name;
    const view = getPivotView(currentReport);
    const setPivotMode = (mode) => setCurrentReport((prev) => ({ ...prev, pivot: { ...prev?.pivot, mode } }));

    return (
        <Box sx={{ width: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
                {name && <Subheading>{name}</Subheading>}
                    <RhDateSelector currentReport={dateReport} setCurrentReport={setDateReport} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <RhFilter currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    <RhPivotModeToggle
                        mode={view.mode}
                        onChange={setPivotMode}
                        sx={{
                            main: {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            }
                        }}
                    />
                    <RhPivotSettings currentReport={currentReport} setCurrentReport={setCurrentReport} chartOption={chartOption} />
                </Box>
            </Box>
            <RhPivotTable
                data={biData?.pivot}
                chartOption={chartOption}
                currentReport={currentReport}
                setCurrentReport={setCurrentReport}
                isError={isError}
                isLoading={isLoading}
                heatmapMode={view.heatmap}
                sx={{
                    main: {
                        p:0,
                        "&:before": null,
                        "&:after": null,
                    }
                }}
            />
        </Box>
    );
};

// Each pivot cell scopes its own filter registry so multiple reports on one dashboard don't collide.
export const RhDashboardPivotFull = ({ chart }) => (
    <ReportHubFilterProvider>
        <RhDashboardPivotFullInner chart={chart} />
    </ReportHubFilterProvider>
);
